"use client"

import { useCallback } from "react"

/**
 * Fetches the privacy policy HTML from the public directory.
 * @returns The HTML string, or a fallback error message on failure.
 */
export async function fetchPrivacyPolicyHtml(): Promise<string> {
  try {
    const response = await fetch("/privacy.html", {
      headers: { "Content-Type": "text/html" },
    })
    return await response.text()
  } catch {
    return "Privacy policy could not be loaded."
  }
}

/**
 * Whether a scrollable element has been scrolled to (or near) its bottom.
 * Content that fits without scrolling is treated as already at the end.
 */
export function hasScrolledToEnd(
  element: Pick<HTMLElement, "scrollTop" | "clientHeight" | "scrollHeight">,
  thresholdPx = 8
): boolean {
  return (
    element.scrollTop + element.clientHeight >=
    element.scrollHeight - thresholdPx
  )
}

/**
 * Returns a ref callback that forces all <a> tags inside the container
 * to open in a new tab (`target="_blank"` + `rel="noopener noreferrer"`).
 *
 * Useful for containers that render raw HTML via `dangerouslySetInnerHTML`.
 */
export function useExternalLinks() {
  return useCallback((node: HTMLDivElement | null) => {
    if (!node) return
    node.querySelectorAll("a").forEach((a) => {
      a.setAttribute("target", "_blank")
      a.setAttribute("rel", "noopener noreferrer")
    })
  }, [])
}
